import {BaseModel} from "../base";
import {SlotSet} from "../../collection/sets/slot_set";
import {BookingSet} from "../../collection/sets/booking_set";
import {AdminConversationModel} from "./conversation";
import {bbTimeZone} from "../../utils/time_zone";

class AdminBookingModel extends BaseModel {

    constructor(data) {
        super(...arguments);
        this.type = 'Admin_Booking';
        this.datetime = moment(this.datetime);
        this.start = this.datetime;
        this.end = moment(this.end_datetime);
        if (!this.end) {
            this.end = this.datetime.clone().add(this.duration, 'minutes');
        }
        this.title = this.full_describe;
        this.time = (this.start.hour() * 60) + this.start.minute();
        // this.startEditable  = false
        // this.durationEditable  = false
        // set to all day if it's a 24 hours span
        this.allDay = this.duration_span && this.duration_span % 86400 == 0;

        if (this.status === 3) {
            this.startEditable = true;
            this.durationEditable = true;
            this.className = 'status_blocked';
        } else if (this.status === 4) {
            this.className = 'status_booked';
        } else if (this.status === 0) {
            this.className = 'status_available';
        }
        if (this.current_multi_status) {
            this.className = `status_${this.current_multi_status}`;
        }
    }


    useFullTime() {
        this.using_full_time = true;
        if (this.pre_time) {
            this.start = this.datetime.clone().subtract(this.pre_time, 'minutes');
        }
        if (this.post_time) {
            return this.end = this.datetime.clone().add(this.duration + this.post_time, 'minutes');
        }
    }

    fullStart() {
        if (this.using_full_time){
            return this.start;
        } else {
            return this.start.clone().subtract((this.pre_time || 0), 'minutes');
        }
    }

    fullEnd() {
        if (this.using_full_time){
            return this.end;
        } else {
            return this.end.clone().add((this.post_time || 0), 'minutes');
        }
    }

    fullDuration(){
        return this.duration + (this.pre_time || 0) + (this.post_time || 0);
    }


    hasPreOrPostTime() {
       return ((this.pre_time && this.pre_time > 0) || (this.post_time && this.post_time > 0)) 
    }

    getPostData() {
        const data = {};
        if (this.date && this.time) {
            data.date = bbTimeZone.toCompanyDate(this.date.date);
            data.time = this.time.time;
            if (this.time.event_id) {
                data.event_id = this.time.event_id;
            } else if (this.time.event_ids) { // what's this about?
                data.event_ids = this.time.event_ids;
            }
        } else {
            this.datetime = this.start.clone();
            if (this.using_full_time) {
                // we need to make sure if @start has changed - that we're adjusting for a possible pre-time
                this.datetime.add(this.pre_time, 'minutes');
            }
            data.date = this.datetime.format('YYYY-MM-DD');
            data.time = (this.datetime.hour() * 60) + this.datetime.minute();
        }
        data.duration = this.duration;
        data.id = this.id;
        data.pre_time = this.pre_time;
        data.post_time = this.post_time;
        data.person_id = this.person_id;
        data.resource_id = this.resource_id;
        data.service_id = this.service_id;
        data.child_client_ids = this.child_client_ids;
        data.people_ids = this.people_ids;
        data.status = this.status;

        if (this.questions) {
            data.questions = (Array.from(this.questions).map(q => q.getPostData()));
        }
        return data;
    }

    hasStatus(status) {
        return (this.multi_status[status] != null);
    }

    labelStatus() {
        return BBLabelStatus.labelStatus(this.current_multi_status);
    }

    statusTime(status) {
        if (this.multi_status[status]) {
            return moment(this.multi_status[status]);
        } else {
            return null;
        }
    }

    sinceStatus(status) {
        const s = this.statusTime(status);
        if (!s) {
            return 0;
        }
        return Math.floor((moment().unix() - s.unix()) / 60);
    }

    sinceStart(options) {
        let s;
        const start = this.datetime.unix();
        if (!options) {
            return Math.floor((moment().unix() - start) / 60);
        }
        if (options.later) {
            s = this.statusTime(options.later).unix();
            if (s > start) {
                return Math.floor((moment().unix() - s) / 60);
            }
        }
        if (options.earlier) {
            s = this.statusTime(options.earlier).unix();
            if (s < start) {
                return Math.floor((moment().unix() - s) / 60);
            }
        }
        return Math.floor((moment().unix() - start) / 60);
    }

    answer(q) {
        if (this.answers_summary) {
            for (const a of Array.from(this.answers_summary)) {
                if (a.name === q) {
                    return a.answer;
                }
            }
        }
        return null;
    }

    $update(data) {
        return new Promise((resolve, reject) => {
            if (data && data.datetime) {
                data.datetime = moment(data.datetime);
                data.datetime.tz();
                data.datetime = data.datetime.format();
            }
            if (!data) {
                data = this.getPostData();
            }

            BookingSet.ignoreUpdate(this); // ignore udpates on this while we're updated
            this.$put('self', {}, data).then((res) => {
                    BookingSet.ignoreUpdate(this, this.updated_at);  // not only ignor updates earlier than the current updated at
                    this.constructor(res);
                    if (this.using_full_time) {
                        this.useFullTime();
                    }

                    SlotSet.updateAll(this.$link('slot').href, this.company_id);
                    return resolve(this);
                }
                , err => reject(err));
        });
    }

    $refetch() {
        return new Promise((resolve, reject) => {
            this.$flush('self');
            this.$get('self').then((res) => {
                    this.constructor(res);
                    if (this.using_full_time) {
                        this.useFullTime();
                    }
                    SlotSet.updateAll(this.$link('slot').href, this.company_id);
                    return resolve(this);
                }
                , err => reject(err));
        });
    }

    $getConversation() {
        return new Promise((resolve, reject) => {

            this
                .$get('conversation')
                .then((data) => {
                    data = new AdminConversationModel(data);

                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                });

        });
    }

    $cancel(reason) {
        return new Promise((resolve, reject) => {

            this
                .$post('cancel', {}, {cancel_reason: reason})
                .then((response) => {
                    this.updateModel(response);
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });

        });
    }
}


export {
    AdminBookingModel
};
